import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { useNavigate } from 'react-router-dom';
import {
  AdminCustomer,
  AdminUser,
  adminListCustomers,
  adminUpdateUser,
  readAxiosDetail,
} from './api_calls';
import { AppContext } from '../../context/context';
import { useContext } from 'react';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
interface UserDetailDrawerProps {
  user: AdminUser | null;
  onClose: () => void;
  onUserUpdated?: (user: AdminUser) => void;
}

export default function UserDetailDrawer({ user, onClose, onUserUpdated }: UserDetailDrawerProps) {
  const appCtx = useContext(AppContext);
  const navigate = useNavigate();
  const currentUserId = appCtx?.userData?.userDetails?.userId;

  const [customers, setCustomers] = useState<AdminCustomer[]>([]);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCustomers = useCallback(async () => {
    const list = await adminListCustomers();
    setCustomers(list);
  }, []);

  useEffect(() => {
    if (!user) {
      setCustomerId(null);
      setError(null);
      return;
    }
    setCustomerId(user.customer_id);
    setIsAdmin(user.role === 'admin');
    setDisabled(user.disabled);
    setError(null);
    void loadCustomers().catch((e) => setError(readAxiosDetail(e)));
  }, [user, loadCustomers]);

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await adminUpdateUser(user.user_id, {
        is_admin: isAdmin,
        customer_id: customerId,
        disabled,
      });
      onUserUpdated?.(updated);
      onClose();
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setSaving(false);
    }
  };

  const isSelf = user?.user_id === currentUserId;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog open={Boolean(user)} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">User</Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <Icon size={20} name="xmark" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!user ? null : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2">
              <strong>Email:</strong> {user.email}
            </Typography>
            <Typography variant="body2">
              <strong>Name:</strong> {user.full_name || '—'}
            </Typography>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Customer
              </Typography>
              <Select
                fullWidth
                size="small"
                value={customerId ?? ''}
                onChange={(e) => setCustomerId(e.target.value === '' ? null : e.target.value)}
                displayEmpty
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {customers.map((c) => (
                  <MenuItem key={c.customer_id} value={c.customer_id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={isAdmin}
                  onChange={(e) => setIsAdmin(e.target.checked)}
                  disabled={isSelf && user.role === 'admin'}
                />
              }
              label="Administrator"
            />
            {isSelf && user.role === 'admin' && (
              <Typography variant="caption" color="text.secondary">
                You cannot remove your own admin status (API restriction).
              </Typography>
            )}
            <FormControlLabel
              control={<Switch checked={disabled} onChange={(e) => setDisabled(e.target.checked)} />}
              label="Account disabled"
            />
            <Typography variant="caption" color="text.secondary">
              Feature access for this user follows their customer&apos;s flag overrides and JWT claims after
              re-login.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Button
          startIcon={<Icon size={20} d={ICON_FALLBACKS.Insights} />}
          disabled={!user}
          onClick={() => {
            if (!user) return;
            onClose();
            navigate(`/admin?tab=analytics&view=events&actorId=${encodeURIComponent(user.user_id)}`);
          }}
        >
          View activity
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void handleSave()} disabled={saving || !user}>
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
